<template>
    <MenuBar>
        <template #start>
            <button
                type="button"
                @click="toggleSideBar"
                @mouseenter="isHovering = ui.showSidebar ? false : true"
            >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <path
                        fill="var(--text-color)"
                        fill-rule="evenodd"
                        d="M10 7h8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-8V7ZM9 7H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3V7ZM4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
            </button>
            <div id="icon_div">
                <RouterLink
                    :to="
                        stek
                            ? {
                                  name: 'stekPage',
                                  params: {
                                      stekName: stek.name,
                                  },
                              }
                            : '/'
                    "
                >
                    <Logo
                        id="logo"
                        v-if="!organization || organization.name === 'Stekz'"
                    />
                    <h1 v-else>{{ organization.name }}</h1>
                </RouterLink>
            </div>
            <Breadcrumb v-if="showBreadcrumbs" />
        </template>
        <template #end>
            <ButtonGroup>
                <SyncStatus />
                <EditViewButton v-if="isViewingLatestVersion" />
                <SettingsMenu />
            </ButtonGroup>
        </template>
    </MenuBar>

    <div
        class="page-with-sidebar"
        :class="{ 'collapsed': ui.showSidebar === false ? true : false }"
    >
        <div ref="sidebar" id="sidebar" :class="{ 'hovering': isHovering }">
            <NavSideBar />
            <div
                :class="{
                    'resize-handle': true,
                    'resizing': isDragging,
                }"
                @mousedown.prevent="startResize"
            />
        </div>
        <div
            class="main-container"
            :class="{ 'show-history-sidebar': showHistory }"
        >
            <div class="page">
                <RouterView />
            </div>
            <div v-if="showHistory" class="history-sidebar">
                <ArtefactHistory />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import ArtefactHistory from "@/components/ArtefactHistory.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import NavSideBar from "@/components/NavSideBar.vue";
import EditViewButton from "@/components/EditViewButton.vue";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useUIStore } from "@/stores/ui-store";
import { useMouse } from "@vueuse/core";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";
import Logo from "../assets/logo.svg?component";
import SettingsMenu from "./menus/SettingsMenu.vue";
import SyncStatus from "@/components/SyncStatus.vue";
import { useStekManager } from "@/composables/stek-manager";
import { useHistoryManager } from "@/stores/history";

const route = useRoute();
const ui = useUIStore();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();

// Note, avoid importing 'showHistory' from the history manager. Somehow
// it will cause the route in the computed artefact in the history manager
// store to be undefined and unreactive.
const showHistory = computed(
    () => "version" in route.params && route.params.version
);

const { isViewingLatestVersion } = useHistoryManager();

const toggleSideBar = () => {
    ui.toggleSideBar();
};

const showBreadcrumbs = computed(() => "breadcrumbs" in route.meta);

const sidebar = useTemplateRef<HTMLElement>("sidebar");

const isHovering = ref(false);
const isDragging = ref(false);
const { x } = useMouse();

const startResize = () => {
    isDragging.value = true;
    document.addEventListener("mousemove", handleResize);
    document.addEventListener("mouseup", stopResize);
};

const handleResize = (e: MouseEvent) => {
    if (isDragging.value && sidebar.value) {
        const newWidth = e.pageX;
        if (newWidth >= 120 && newWidth <= 500) {
            sidebar.value.style.width = `${newWidth}px`;
            sidebar.value.style.flexBasis = `${newWidth}px`;
            ui.sidebarWidth = newWidth;
        }
    }
};

const stopResize = () => {
    isDragging.value = false;
    document.removeEventListener("mousemove", handleResize);
    document.removeEventListener("mouseup", stopResize);
};

watch(x, () => {
    // if the sidebar is collapsed, make it visible (but still collapsed)
    if (x.value < 5 && !ui.showSidebar) {
        isHovering.value = true;
    }
    // Turn hovering off when mouse x leaves sidebar
    if (
        !isDragging.value &&
        isHovering.value &&
        x.value > ui.sidebarWidth + 10
    ) {
        isHovering.value = false;
    }
});

onMounted(() => {
    if (sidebar.value) {
        sidebar.value.style.width = `${ui.sidebarWidth}px`;
        sidebar.value.style.flexBasis = `${ui.sidebarWidth}px`;
    }
});
</script>

<style scoped lang="scss">
.p-menubar {
    position: fixed;
    width: 100%;
    z-index: 1000;

    :deep(.p-menubar-start) {
        overflow-x: auto;
    }
}

#icon_div {
    align-items: center;
    justify-content: center;
    padding-left: 8px;
    padding-right: 8px;

    h1 {
        margin: 0;
        color: black;
        padding: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    a {
        text-decoration: none;
        text-decoration: inherit;
    }

    #logo {
        display: block;
        height: 20px;
        max-width: 175px;
    }
}

.page-with-sidebar {
    display: flex;
    flex-direction: row;

    // make room for the fixed menubar on top
    #sidebar,
    .main-container {
        padding-top: 60px;
        position: relative;
    }

    &.collapsed {
        #sidebar {
            z-index: 600;
            position: fixed;

            &:not(.hovering) {
                transform: translateX(-100%);
            }
        }
    }

    #sidebar {
        height: 100vh;
        background: var(--p-menubar-background);
        border-right: 1px solid var(--p-menubar-border-color);
        display: flex;
        flex-direction: column;
        flex-grow: 0;
        flex-shrink: 0;
        left: 0;
        min-width: 120px;
        max-width: 500px;
        overflow-y: hidden; // note we need this for the default resize handle
        position: relative; /* Needed for absolute positioning of handle */
        transform: translateX(0);
        transition-property: transform;
        transition-timing-function: ease;
        transition-duration: 200ms;

        &.on-top {
            z-index: 600;
            overflow: visible;
        }
    }

    .main-container {
        width: 100%;
        height: 100vh;
        overflow-y: scroll;
        flex-grow: 1;
        display: flex;
        flex-direction: column;

        &.show-history-sidebar {
            flex-direction: row;
        }

        .history-sidebar {
            display: flex;
            flex: 0 1 20rem;
            border-left: 1px solid var(--palette-grey-300);
            flex-direction: column;
        }
    }
}

.page {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    padding: 1rem;
    gap: 1rem;
}

.resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 3px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    transition: 100ms 50ms ease-in-out;

    &:hover,
    &.resizing {
        background: var(--stekz-primary-background);
    }
}

.popover-container {
    .info-text {
        padding: 1rem;
        text-align: center;
    }

    :deep(.p-datatable) {
        .sync-action-buttons {
            display: flex;
            gap: 8px;
        }
    }
}
</style>
